import {
  Activity,
  Building2,
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  Filter,
  Package2,
  User,
  UserCheck,
  MoreHorizontal,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import StatusBadge from "../../../components/shared/StatusBadge";
import TruncatedText from "../../../components/shared/TruncatedText";
import { Badge } from "../../../components/ui/badge";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { formatDate } from "../../../utils/dateUtils";

const isTaskOverdue = (dueDate, status) => {
  if (!dueDate || status === "COMPLETED") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const taskDueDate = new Date(dueDate);
  taskDueDate.setHours(0, 0, 0, 0);
  return taskDueDate < today;
};

const TaskTable = ({
  tasks,
  permissions,
  filters,
  onFilterChange,
  onEdit,
  onDelete,
  onStatusUpdate,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const navigate = useNavigate();

  const taskColumns = [
    {
      header: "Task Name",
      accessor: "name",
      isPrimary: true,
      wrap: true,
      render: (val, task) => (
        <div className="flex flex-col gap-1 max-w-[200px] md:max-w-[250px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="md:text-sm md:font-semibold text-yale-blue-900 dark:text-slate-100 cursor-pointer hover:text-yale-blue-600 transition-colors whitespace-normal break-words">
              {task.title}
            </span>
            {task.priority === "HIGH" && (
              <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
                High Priority
              </Badge>
            )}
            {task.deleted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                Deleted
              </span>
            )}
          </div>
          {task.description && (
            <TruncatedText text={task.description} maxLength={45} className="text-xs text-slate-500 dark:text-slate-400" />
          )}
        </div>
      )
    },
    {
      header: "Project",
      accessor: "projectId",
      render: (val, task) => {
        const project = task.project;
        return (
          <div
            className="text-sm text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 cursor-pointer font-medium flex items-start gap-1.5 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${task.project.id}`);
            }}
          >
            <Building2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yale-blue-500" />
            <TruncatedText text={project?.name || "N/A"} lines={2} className="leading-tight" />
          </div>
        );
      }
    },
    {
      header: "Milestone",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.milestone !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="end">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Milestone</p>
              <LazySearchSelect
                value={filters.milestone}
                onChange={(val) => onFilterChange("milestone", val)}
                apiUrl="/milestones"
                dataKey="items"
                labelKey="name"
                valueKey="id"
                placeholder="Select milestone"
                searchPlaceholder="Search milestones..."
                showAllOption={true}
                allOptionLabel="All Milestones"
                allOptionValue="ALL"
                extraParams={{
                  projectId: filters.projectId && filters.projectId !== "ALL" ? filters.projectId : undefined
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "milestone",
      render: (val, task) => {
        const milestone = task.milestone;
        return (
          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
            <Package2 className="w-3.5 h-3.5 text-yale-blue-500" />
            <TruncatedText text={milestone?.name || "N/A"} lines={1} className="leading-tight" />
          </div>
        );
      }
    },
    {
      header: "Assigned To",
      accessor: "assignedTo",
      render: (val, task) => {
        const assignedUser = task.assignedTo;
        return (
          <div className="flex items-center gap-2">
            <div className="p-1 bg-slate-100 dark:bg-yale-blue-800 rounded-full shrink-0">
              <User className="w-3 h-3 text-yale-blue-500" />
            </div>
            <span className="text-sm text-slate-700 dark:text-slate-300">{assignedUser?.name || "Unassigned"}</span>
          </div>
        );
      }
    },
    {
      header: "Start Date",
      accessor: "startDate",
      sortable: true,
      sortKey: "startDate",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Calendar className="w-3.5 h-3.5 text-yale-blue-500" />
          {formatDate(val) || "N/A"}
        </div>
      )
    },
    {
      header: "Due Date",
      accessor: "dueDate",
      sortable: true,
      sortKey: "dueDate",
      render: (val, task) => {
        const overdue = isTaskOverdue(val, task.status);
        return (
          <div
            className={`flex items-center gap-1.5 text-sm ${overdue ? "text-red-600 font-bold" : "text-slate-600 dark:text-slate-400"}`}
          >
            <Clock className={`w-3.5 h-3.5 ${overdue ? "text-red-600" : "text-yale-blue-500"}`} />
            {formatDate(val) || "N/A"}
          </div>
        );
      }
    },
    {
      header: "Status",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.status !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-4" align="end">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Status</p>
              <Select value={filters.status || "ALL"} onValueChange={(val) => onFilterChange("status", val)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "status",
      render: (val, task) => (
        <div className="flex flex-col gap-1">
          <div>
            <StatusBadge status={val} />
          </div>
          <div>{isTaskOverdue(task.dueDate, val) && <StatusBadge status="overdue" />}</div>
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-tasks-table"
              />
              <Label htmlFor="show-deleted-tasks-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, task) => (
        <ActionMenu
          canEdit={permissions.canEdit && !task.deleted}
          canDelete={permissions.canDelete && !task.deleted}
          onEdit={() => onEdit(task.id)}
          onDelete={() => onDelete(task.id)}
          menuWidth="w-56"
          quickLinksLabel="Project Links"
          customActions={
            task.status !== "COMPLETED" && !task.deleted ? (
              <>
                <DropdownMenuItem
                  onClick={() => onStatusUpdate(task.id, "IN_PROGRESS")}
                  disabled={task.status === "IN_PROGRESS"}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Clock className="w-4 h-4 text-yale-blue-500" />
                  <span>Start Task</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onStatusUpdate(task.id, "COMPLETED")}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Mark Completed</span>
                </DropdownMenuItem>
              </>
            ) : null
          }
          quickLinks={
            <>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/activities?projectId=${task.projectId}`)}>
                <Activity className="w-4 h-4 mr-2" /> Activities
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/materials?projectId=${task.projectId}`)}>
                <Package2 className="w-4 h-4 mr-2" /> Materials
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/attendance?projectId=${task.projectId}`)}>
                <UserCheck className="w-4 h-4 mr-2" /> Attendance
              </DropdownMenuItem>
            </>
          }
        />
      )
    }
  ];

  return (
    <DataTable
      columns={taskColumns}
      data={tasks}
      emptyStateTitle="No tasks found"
      emptyStateDescription="No tasks match the selected filters. Click 'Create Task' to add your first task."
      emptyStateIcon={ClipboardList}
      getRowClassName={(task) => (isTaskOverdue(task.dueDate, task.status) ? "bg-red-50/30 dark:bg-red-900/10" : "")}
      sortBy={filters.sortBy}
      direction={filters.direction}
      onSort={(key, dir) => {
        onFilterChange("sortBy", key);
        onFilterChange("direction", dir);
      }}
    />
  );
};

export default TaskTable;
